import {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ReactSVG } from 'react-svg'

import { cn } from '@shared/lib/cn'

import styles from './styles.module.scss'



export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  inputSize?: 'small' | 'medium' | 'large' | 'extra_large';
  theme?: 'grey';
  value?: string;
  isInvalid?: boolean;
  startDecorator?: ReactNode;
  startDecoratorClassName?: string;
  endDecorator?: ReactNode;
  endDecoratorClassName?: string;
  hideClearButton?: boolean;
  hideErrorIcon?: boolean;
  inputClassName?: string;
  onCustomClear?: () => void;
  onlyNumbers?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    inputSize = 'medium',
    value,
    theme,
    className,
    startDecorator,
    startDecoratorClassName,
    endDecorator,
    endDecoratorClassName,
    hideErrorIcon = false,
    isInvalid = false,
    hideClearButton = false,
    inputClassName,
    onCustomClear,
    onlyNumbers = false,
    ...rest
  } = props
  const [ isFilled, setIsFilled ] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setIsFilled(!!value)
  }, [ value ])

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (onlyNumbers && /\D/.test(e.target.value)) return
    setIsFilled(!!e.target.value)
  }

  const handleClear = () => {
    const target =
      inputRef.current ?? (ref && 'current' in ref ? ref.current : null)

    if (target) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set
      nativeInputValueSetter?.call(target, '')

      const inputEvent = new Event('input', { bubbles: true })
      const blurEvent = new Event('blur', { bubbles: true })
      target.dispatchEvent(inputEvent)
      target.dispatchEvent(blurEvent)
      setIsFilled(false)

      target.focus()
    }

    if (onCustomClear) {
      onCustomClear()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      onlyNumbers &&
      !/[0-9]/.test(e.key) &&
      e.key !== 'Backspace' &&
      e.key !== 'Delete' &&
      e.key !== 'ArrowLeft' &&
      e.key !== 'ArrowRight' &&
      e.key !== 'Tab'
    ) {
      e.preventDefault()
    }
  }

  return (
    <div
      className={cn(styles.root, styles[inputSize], className, {
        [styles._error]: isInvalid,
        [styles._filled]: isFilled,
        [styles[`_${theme}`]]: theme,
      })}
    >
      {startDecorator && (
        <div
          className={cn(
            styles['root__decorator-start'],
            startDecoratorClassName,
          )}
        >
          {startDecorator}
        </div>
      )}

      {endDecorator && (
        <div
          className={cn(styles['root__decorator-end'], endDecoratorClassName)}
        >
          {endDecorator}
        </div>
      )}

      <input
        ref={ref ?? inputRef}
        className={cn('reset-input', styles.input, inputClassName)}
        type="text"
        value={value}
        onChange={(e) => {
          handleInput(e)
          if (rest.onChange) rest.onChange(e)
        }}
        onKeyDown={handleKeyDown}
        {...rest}
      />

      {isFilled && !isInvalid && !endDecorator && !hideClearButton && (
        <button
          type="button"
          className={cn('reset-button', styles.reset)}
          onClick={handleClear}
        >
          <ReactSVG src="/icons/circle-x.svg" wrapper="span" />
        </button>
      )}
      {isInvalid && !hideErrorIcon && (
        <ReactSVG
          className={styles['icon-error']}
          src="/icons/info-circle.svg"
          wrapper="span"
        />
      )}
    </div>
  )
})

Input.displayName = 'Input'

export default Input
